import { useMountEffect } from "@libs";
import { removeDownloadSnack } from "@modules";
import SnackbarContent from "@mui/material/SnackbarContent";
import { GenButton } from "@ui";
import React, { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

export interface DownloadSnackBarProps {
  id: string;
  pendingMessage?: string;
  completedMessage: string;
  downloadLink?: string;
  fileName?: string;
  countdownSeconds?: number;
  onClose?: () => void;
  onDownload?: () => void | Promise<void>;
}

const DownloadSnackBar: FC<DownloadSnackBarProps> = (props) => {
  const {
    id,
    pendingMessage = "Preparing your download",
    completedMessage,
    downloadLink,
    fileName,
    countdownSeconds,
    onClose,
    onDownload,
  } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [second, setSecond] = useState(
    downloadLink ? 0 : countdownSeconds || 10,
  );
  const [open, setOpen] = useState(true);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

  useMountEffect(() => {
    if (downloadLink) {
      handleDownload();
      return;
    }
    const id = setInterval(() => {
      setSecond((prev) => {
        if (prev === 1) {
          handleDownload();
          clearInterval(id);
        }
        return prev - 1;
      });
    }, 1000);
    setIntervalId(id);
  });

  const handleClose = useCallback(() => {
    setOpen(false);
    if (intervalId) clearInterval(intervalId);
    setSecond(0);
    onClose?.();
    dispatch(removeDownloadSnack(id));
  }, [dispatch, id, intervalId, onClose]);

  const handleDownload = useCallback(async () => {
    if (downloadLink) {
      const downloadUrl = downloadLink || "#";
      // Trigger file download
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = downloadUrl;
      link.download = `${fileName ?? "download"}`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (onDownload) {
      await onDownload();
    }
    // // get download filestream from BE

    handleClose();
  }, [downloadLink, fileName, handleClose, onDownload]);

  const action = useMemo(() => {
    return (
      <>
        {
          <GenButton
            onClick={handleClose}
            variant="outlined"
            color="primary"
            size="small"
          >
            {t("common.cancel")}
          </GenButton>
        }
      </>
    );
  }, [t, handleClose]);

  return open ? (
    <SnackbarContent
      message={second > 0 ? `${pendingMessage}` : completedMessage}
      action={action}
    />
  ) : null;
};

export default DownloadSnackBar;
